import { AlertTriangle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import useAppStore from "@/stores/useAppStore";
import { UserStatusEnum } from "@/types/enum";
import { useNavigate } from "react-router-dom";

function AccountStatusPage() {
  const user = useAppStore((s) => s.user);
  const resetAppStore = useAppStore((s) => s.resetAppStore);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("app_accessToken");
    localStorage.removeItem("app_refreshToken");
    resetAppStore();
    navigate("/login");
  };

  const getStatusMessage = () => {
    switch (user?.status) {
      case UserStatusEnum.INACTIVE:
        return {
          title: "Account Inactive",
          message:
            "Your account has been deactivated. Please contact our support team to reactivate your account.",
          color: "text-orange-600",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
        };
      case UserStatusEnum.SUSPENDED:
        return {
          title: "Account Suspended",
          message:
            "Your account has been suspended due to policy violations. Please contact our support team for assistance.",
          color: "text-red-600",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
        };
      default:
        return {
          title: "Account Issue",
          message:
            "There is an issue with your account. Please contact our support team.",
          color: "text-gray-600",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
        };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <AlertTriangle className={`mx-auto h-16 w-16 ${statusInfo.color}`} />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            {statusInfo.title}
          </h2>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Status Alert */}
          <div
            className={`rounded-md p-4 mb-6 ${statusInfo.bgColor} border ${statusInfo.borderColor}`}
          >
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className={`h-5 w-5 ${statusInfo.color}`} />
              </div>
              <div className="ml-3">
                <p className={`text-sm ${statusInfo.color}`}>
                  {statusInfo.message}
                </p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Account Details
            </h3>
            <p className="text-sm text-gray-600">Name: {user?.name}</p>
            <p className="text-sm text-gray-600">Email: {user?.email}</p>
            <p className="text-sm text-gray-600">
              Status: <span className="capitalize">{user?.status}</span>
            </p>
          </div>

          {/* Contact Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
              Contact Support
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {/* Email Support */}
              <a
                href="mailto:it@xpert.consulting?subject=Logistics%20Planning%20Tool%20//%20Account%20Reactivation%20Request"
                className="flex items-center justify-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Mail className="h-5 w-5 text-blue-600 mr-3" />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    Email Support
                  </p>
                  <p className="text-xs text-gray-600">it@xpert.consulting</p>
                </div>
              </a>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              When contacting support:
            </h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Include your full name and email address</li>
              <li>• Mention your account status issue</li>
              <li>• Provide any relevant details about your situation</li>
              <li>• Our team typically responds within 24 hours</li>
            </ul>
          </div>

          {/* Logout Button */}
          <div className="mt-6">
            <Button onClick={handleLogout} variant="outline" className="w-full">
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
          © 2024 Xpert Consulting. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default AccountStatusPage;
